/**
 * GameBackground - Full-screen background for game sessions
 *
 * Shows the module hero image as default background, then transitions
 * to the scene image/video when one is available from the backend.
 *
 * Features:
 * - Uses module hero image as initial background
 * - Transitions to scene video (looped) or image when available
 * - Smooth crossfade between backgrounds
 * - Semi-transparent overlay for UI legibility
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useGameStore } from '@/lib/stores/gameStore';
import { getDynamicImageUrl } from '@/lib/media';
import ManagedVideo from '@/components/common/ManagedVideo';

/** Duel portrait data stored in localStorage for training sessions */
interface DuelPortraits {
  player?: { charId?: string; name?: string; imageUrl?: string };
  opponent?: { charId?: string; name?: string; imageUrl?: string };
}

interface MediaState {
  url: string;
  type: 'video' | 'image';
}

export default function GameBackground() {
  const moduleHeroImage = useGameStore((s) => s.moduleHeroImage);
  const currentScene = useGameStore((s) => s.currentScene);
  const sceneCharacters = useGameStore((s) => s.sceneCharacters);
  const { pathname } = useLocation();

  // Stable boolean: are there living hostile creatures in the scene?
  const hasLivingHostiles = sceneCharacters.some(
    (c) => c.healthState !== 'dead' && (c.type === 'monster' || c.attitude === 'hostile')
  );

  // Training duel detection — read portraits from localStorage
  const isTrainingSession = pathname.includes('party_training_');
  const duelPortraits = useMemo<DuelPortraits | null>(() => {
    if (!isTrainingSession) return null;
    try {
      const raw = localStorage.getItem('game-session-duel-portraits');
      if (!raw) {
        console.log('[duel-loader] GameBackground: no localStorage for duel portraits');
        return null;
      }
      const parsed = JSON.parse(raw) as DuelPortraits;
      console.log('[duel-loader] GameBackground duelPortraits parsed', parsed);
      return parsed;
    } catch (e) {
      console.warn('[duel-loader] GameBackground duelPortraits parse failed', e);
      return null;
    }
  }, [isTrainingSession]);

  // Current displayed media (stays visible during loading)
  const [currentMedia, setCurrentMedia] = useState<MediaState | null>(null);
  // Next media being loaded (for crossfade)
  const [nextMedia, setNextMedia] = useState<MediaState | null>(null);
  const [isNextMediaLoaded, setIsNextMediaLoaded] = useState(false);
  // Track the most recent nextMedia to prevent race conditions during crossfade
  const nextMediaRef = useRef<MediaState | null>(null);

  // Build full URL from relative path - all media comes from GCS bucket
  const buildFullUrl = useCallback((url: string): string => {
    if (!url) return '';
    // Full URLs (GCS) - return as-is
    if (url.startsWith('http')) return url;
    // Dynamic images are served from GCS
    return getDynamicImageUrl(url);
  }, []);

  // Keep nextMediaRef in sync with nextMedia state
  useEffect(() => {
    nextMediaRef.current = nextMedia;
  }, [nextMedia]);

  // Determine which media to show: scene video > scene image > encounter image > module hero
  useEffect(() => {
    const showEncounter = hasLivingHostiles && !!currentScene?.encounterVideoUrl;
    const encounterVideoUrl = showEncounter ? currentScene?.encounterVideoUrl : undefined;
    const encounterImageUrl =
      hasLivingHostiles && !encounterVideoUrl ? currentScene?.encounterImageUrl : undefined;
    const sceneVideoUrl = !hasLivingHostiles ? currentScene?.videoUrl : undefined;
    const sceneImageUrl = !hasLivingHostiles ? currentScene?.imageUrl : undefined;

    // Priority: encounter video > encounter image (combat) > scene video > scene image > module hero
    const targetUrl =
      encounterVideoUrl || encounterImageUrl || sceneVideoUrl || sceneImageUrl || moduleHeroImage;
    const targetType: 'video' | 'image' =
      (encounterVideoUrl && targetUrl === encounterVideoUrl) ||
      (sceneVideoUrl && targetUrl === sceneVideoUrl)
        ? 'video'
        : 'image';

    if (!targetUrl) return;

    const fullUrl = buildFullUrl(targetUrl);
    if (!fullUrl) return;

    // Skip if same as current or already loading
    if (currentMedia?.url === fullUrl) return;
    if (nextMedia?.url === fullUrl) return;

    // Set up next media for crossfade
    setNextMedia({ url: fullUrl, type: targetType });
    setIsNextMediaLoaded(false);
  }, [
    currentScene?.imageUrl,
    currentScene?.videoUrl,
    currentScene?.encounterImageUrl,
    currentScene?.encounterVideoUrl,
    hasLivingHostiles,
    moduleHeroImage,
    buildFullUrl,
    currentMedia?.url,
    nextMedia?.url,
  ]);

  // Handle crossfade when next media is loaded
  useEffect(() => {
    if (nextMedia && isNextMediaLoaded) {
      const mediaToApply = nextMedia;
      // Brief delay for smooth transition
      const timer = setTimeout(() => {
        // Only apply if this is still the most recent media (prevents race condition)
        if (nextMediaRef.current === mediaToApply) {
          setCurrentMedia(mediaToApply);
          setNextMedia(null);
          setIsNextMediaLoaded(false);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [nextMedia, isNextMediaLoaded]);

  // Render video element
  const renderVideo = (
    url: string,
    isHidden: boolean,
    onCanPlay?: () => void,
    onError?: () => void
  ) => (
    <ManagedVideo
      src={url}
      className={cn(
        'fixed inset-0 w-full h-full object-cover z-0 transition-opacity duration-700',
        isHidden && 'opacity-0 pointer-events-none'
      )}
      onCanPlay={onCanPlay}
      onError={onError}
    />
  );

  // Render image element
  const renderImage = (url: string, isHidden: boolean) => (
    <div
      className={cn(
        'fixed inset-0 z-0 transition-opacity duration-700',
        isHidden && 'opacity-0 pointer-events-none'
      )}
      style={{
        backgroundImage: `url(${url})`,
        backgroundSize: 'cover',
        backgroundPosition: 'top',
        backgroundRepeat: 'no-repeat',
      }}
    />
  );

  // Render media based on type
  const renderMedia = (
    media: MediaState,
    isHidden: boolean,
    onReady?: () => void,
    onError?: () => void
  ) =>
    media.type === 'video'
      ? renderVideo(media.url, isHidden, onReady, onError)
      : renderImage(media.url, isHidden);

  // No background media — show duel portraits for training or plain dark fallback
  if (!currentMedia && !nextMedia) {
    if (isTrainingSession) {
      console.log('[duel-loader] GameBackground rendering fallback', {
        hasDuelPortraits: !!(
          duelPortraits &&
          (duelPortraits.player?.imageUrl || duelPortraits.opponent?.imageUrl)
        ),
        duelPortraits,
      });
    }
    // Training duel split portrait background
    if (duelPortraits && (duelPortraits.player?.imageUrl || duelPortraits.opponent?.imageUrl)) {
      return (
        <>
          <div className="fixed inset-0 z-0 flex bg-zinc-950">
            {/* Player portrait — left half */}
            <div className="flex-1 relative overflow-hidden">
              {duelPortraits.player?.imageUrl ? (
                <img
                  src={duelPortraits.player.imageUrl}
                  alt={duelPortraits.player.name ?? 'Player'}
                  className="w-full h-full object-cover object-top"
                  style={{ filter: 'brightness(0.7)' }}
                  onError={() => {
                    console.warn(
                      '[duel-loader] GameBackground player portrait failed',
                      duelPortraits.player?.imageUrl
                    );
                  }}
                />
              ) : (
                <div className="w-full h-full bg-zinc-900" />
              )}
              {/* Gradient toward center */}
              <div className="absolute inset-0 bg-gradient-to-l from-black/50 via-transparent to-black/20" />
            </div>

            {/* Opponent portrait — right half */}
            <div className="flex-1 relative overflow-hidden">
              {duelPortraits.opponent?.imageUrl ? (
                <img
                  src={duelPortraits.opponent.imageUrl}
                  alt={duelPortraits.opponent.name ?? 'Opponent'}
                  className="w-full h-full object-cover object-top"
                  style={{ filter: 'brightness(0.7)' }}
                  onError={() => {
                    console.warn(
                      '[duel-loader] GameBackground opponent portrait failed',
                      duelPortraits.opponent?.imageUrl
                    );
                  }}
                />
              ) : (
                <div className="w-full h-full bg-zinc-900" />
              )}
              {/* Gradient toward center */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/20" />
            </div>
          </div>
          {/* Dark overlay — duel is always cinematic dark */}
          <div
            className={cn(
              'fixed inset-0 z-0 pointer-events-none',
              'bg-gradient-to-b from-zinc-950/30 via-transparent to-zinc-950/30'
            )}
          />
        </>
      );
    }

    return (
      <>
        {/* Fallback dark background */}
        <div className="fixed inset-0 z-0 bg-background" />
        {/* Theme-aware overlay */}
        <div
          className={cn(
            'fixed inset-0 z-0 pointer-events-none',
            'bg-gradient-to-b from-background/60 via-background/40 to-background/50'
          )}
        />
      </>
    );
  }

  return (
    <>
      {/* Current background media layer */}
      {currentMedia && renderMedia(currentMedia, false)}

      {/* Next media (for crossfade) - hidden until loaded */}
      {nextMedia &&
        (nextMedia.type === 'video' ? (
          renderVideo(
            nextMedia.url,
            !isNextMediaLoaded,
            () => setIsNextMediaLoaded(true),
            () => {
              console.warn('Failed to load game background video:', nextMedia.url);
              setNextMedia(null);
              setIsNextMediaLoaded(false);
            }
          )
        ) : (
          <>
            {/* Hidden preloader triggers onLoad without painting */}
            <img
              src={nextMedia.url}
              alt=""
              className="hidden"
              onLoad={() => setIsNextMediaLoaded(true)}
              onError={() => {
                console.warn('Failed to load game background image:', nextMedia.url);
                setNextMedia(null);
                setIsNextMediaLoaded(false);
              }}
            />
            {/* Image layer — starts hidden (opacity-0), fades in when loaded */}
            {renderImage(nextMedia.url, !isNextMediaLoaded)}
          </>
        ))}

      {/* Theme-aware overlay for text legibility */}
      <div
        className={cn(
          'fixed inset-0 z-0 pointer-events-none',
          'bg-gradient-to-b from-background/60 via-background/40 to-background/50'
        )}
      />
    </>
  );
}
